/** @module Catalogue data of one card: one cached query per print, shared by every screen. */
import { useQuery } from "@tanstack/react-query";

import { api, type components, problemFrom } from "@/api/client";

export type Card = components["schemas"]["CardPrintOut"];

export async function fetchCard(printId: string): Promise<Card> {
  const { data, response } = await api.GET("/api/v1/cards/{print_id}", {
    params: { path: { print_id: printId } },
  });
  if (!data) throw problemFrom(response);
  return data;
}

/** The same key everywhere, so a card loaded for one tile serves every other place. */
export function useCard(printId: string | null) {
  return useQuery({
    queryKey: ["card", printId],
    queryFn: () => fetchCard(printId ?? ""),
    enabled: Boolean(printId),
    staleTime: 10 * 60_000,
  });
}

/** "SVI 074" – what is printed on the card, so reprints can be told apart. */
export function printedAs(card: Card | undefined, fallback: string): string {
  return card ? `${card.set_code ?? card.set_id} ${card.local_id}` : fallback;
}

/** "74/198" or "74": the number as read from the photo. */
export function readNumber(ocr: { number?: string; total?: string } | undefined): string | null {
  if (!ocr?.number) return null;
  return ocr.total ? `${ocr.number}/${ocr.total}` : ocr.number;
}

/** Full-art classes get the foil edge; the design keeps the holo gradient that rare. */
const FULL_ART = new Set(["illustration", "secret", "full_art"]);

export function isFullArt(rarityClass: string | null | undefined): boolean {
  return FULL_ART.has(rarityClass ?? "");
}

/** "PAR 133/182" for a list row; the TCGdex id only where the set has no printed code. */
export function printedNumber(card: {
  set_id: string;
  set_code?: string | null;
  set_total?: number | null;
  local_id: string;
}): string {
  const total = card.set_total ? `/${String(card.set_total)}` : "";
  return `${card.set_code ?? card.set_id} ${card.local_id}${total}`;
}

/**
 * "reverse:pokeball+stamp" as "Reverse Holo · pokeball · stamp": the type is translated,
 * subtypes and stamps are names TCGdex gives and stay as they are.
 */
export function variantLabel(key: string, translate: (key: string, fallback: string) => string) {
  const [type = key, ...rest] = key.split(/[:+]/);
  return [translate(`variants.${type}`, type), ...rest.map((part) => part.replace("foil=", ""))]
    .filter(Boolean)
    .join(" · ");
}
